import { useInfiniteQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ErrorState, PageContainer, PageHeader, TableSkeleton } from '../../../components/ui/Page';
import { teamApi, teamKeys } from '../../team/api';

const ACTION_FILTERS = [
  { value: '', label: 'All actions' },
  { value: 'auth.', label: 'Sign-ins & security' },
  { value: 'staff.', label: 'Staff' },
  { value: 'rbac.', label: 'Roles & permissions' },
  { value: 'branch.', label: 'Branches' },
  { value: 'department.', label: 'Teams' },
  { value: 'tenant.', label: 'Business settings' },
  { value: 'media.', label: 'Uploads' },
];

/** Immutable audit trail (FR-AUD-02), newest first with cursor paging. */
export function AuditPage() {
  const [action, setAction] = useState('');
  const query = useInfiniteQuery({
    queryKey: teamKeys.audit(action),
    queryFn: ({ pageParam }) =>
      teamApi.audit({
        limit: 50,
        ...(action ? { action } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
  });
  const rows = query.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Audit log"
        description="Every security-relevant change in your account. Entries cannot be edited or deleted."
        actions={
          <label className="flex items-center gap-2 text-sm">
            <span className="text-fg-muted">Show</span>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="h-9 rounded-md border border-line bg-surface px-2 text-sm"
            >
              {ACTION_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        }
      />
      <Card>
        {query.isPending ? (
          <TableSkeleton />
        ) : query.isError ? (
          <div className="p-4">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<ScrollText className="size-6" />} title="No entries" />
        ) : (
          <ol className="divide-y divide-line">
            {rows.map((row) => (
              <li key={row.id} className="px-5 py-3">
                <details>
                  <summary className="flex cursor-pointer list-none flex-wrap items-baseline gap-x-3 gap-y-1">
                    <code className="text-sm font-medium text-fg">{row.action}</code>
                    <span className="text-sm text-fg-muted">{row.actor?.fullName ?? row.actorType}</span>
                    <span className="ml-auto text-xs text-fg-subtle tabular-nums">
                      {new Date(row.createdAt).toLocaleString()}
                    </span>
                  </summary>
                  <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-surface-sunken p-3 text-xs text-fg">
                    {JSON.stringify(
                      {
                        entity: row.entityType && `${row.entityType} ${row.entityId ?? ''}`,
                        before: row.before,
                        after: row.after,
                        metadata: row.metadata,
                      },
                      null,
                      2,
                    )}
                  </pre>
                </details>
              </li>
            ))}
          </ol>
        )}
        {query.hasNextPage ? (
          <div className="border-t border-line p-3 text-center">
            <Button
              variant="secondary"
              size="sm"
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Load older entries
            </Button>
          </div>
        ) : null}
      </Card>
    </PageContainer>
  );
}
